/**
 * Workspace groups (ADR-041) and groups on named labels (ADR-039 amendment 1):
 * the Groups page's data and writes, and a label's Groups section.
 *
 * A group is a named set of a workspace's own members and grants nothing by
 * itself. Put on a named label, it lets its members read the label — each up
 * to their workspace role. The server decides every answer; these hooks only
 * fetch and send.
 *
 * Every group write invalidates BOTH families — the workspace's groups
 * (`queryKeys.vault.workspaceGroupsAll`) and its labels
 * (`queryKeys.vault.namedLabelsAll`) — because a change to a group changes who
 * reads each label it is on, and a change to a label's groups changes how many
 * labels the group reads.
 *
 * SECURITY: the step-up writes (adding someone to a group that reads a label,
 * putting a group on a label) send the person's password and TOTP code, so
 * they go through the untraced `stepUpFetch` — never the traced `api` client,
 * whose request log the report-a-problem dialog exposes.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  VAULT_WORKSPACE_GROUP_DESCRIPTION_MAX,
  VAULT_WORKSPACE_GROUP_NAME_MAX,
  vaultNamedLabelPath,
  vaultWorkspaceGroupPath,
  type VaultNamedLabelGroupDto,
  type VaultWorkspaceGroupMemberAddedDto,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { StepUpFetchError, stepUpFetch } from '@/lib/step-up-fetch';
import type { StepUpCredentials } from '../components/StepUpDialog';
import { describeNamedLabelError, namedLabelErrorCode } from './useVaultNamedLabels';

const proof = (creds: StepUpCredentials) => ({
  currentPassword: creds.currentPassword,
  ...(creds.totpCode ? { totpCode: creds.totpCode } : {}),
});

const MESSAGES: Record<string, string> = {
  VAULT_GROUP_FORBIDDEN: 'Only owners and admins can do that.',
  VAULT_GROUP_NAME_TAKEN: 'There’s already a group with that name here.',
  VAULT_GROUP_NAME_INVALID: `Give the group a name of up to ${VAULT_WORKSPACE_GROUP_NAME_MAX} characters.`,
  VAULT_GROUP_DESCRIPTION_INVALID: `Keep the description to ${VAULT_WORKSPACE_GROUP_DESCRIPTION_MAX} characters.`,
  VAULT_GROUP_LIMIT: 'This workspace has as many groups as it can hold.',
  VAULT_GROUP_READS_LABELS: 'This group reads labels. Take it off the labels it reads first.',
  VAULT_GROUP_MANAGER_REQUIRED: 'Only a manager of this group can do that.',
  VAULT_GROUP_ALREADY_MEMBER: 'They’re already in this group.',
  VAULT_GROUP_MEMBER_NOT_A_MEMBER: 'Only people in this workspace can be in its groups.',
  VAULT_GROUP_MEMBER_LIMIT: 'This group has as many people as it can hold.',
  VAULT_GROUP_LAST_MANAGER:
    'Make someone else a manager first — a group keeps one while it has members.',
  VAULT_GROUP_ORG_ONLY: 'Only shared workspaces have groups.',
  VAULT_GROUPS_NOT_AVAILABLE: 'Groups aren’t available for this workspace yet.',
  NAMED_LABEL_GROUP_ALREADY: 'That group already reads this label.',
  NAMED_LABEL_READER_LIMIT: 'A label this affects already has as many readers as it can hold.',
  NAMED_LABEL_MANAGER_REQUIRED: 'Only a manager of this label can do that.',
  NOT_FOUND: 'Not found — it may have been removed, or you’re not in it.',
};

/** Plain words for a refused group request. */
export function describeWorkspaceGroupError(
  err: unknown,
  fallback = 'Something went wrong.',
): string {
  const code = namedLabelErrorCode(err);
  if (code && MESSAGES[code]) return MESSAGES[code];
  return describeNamedLabelError(err, fallback);
}

/** The codes the step-up dialog explains itself (a missing or wrong password or code). */
const STEP_UP_CODES = new Set([
  'MFA_CODE_REQUIRED',
  'MFA_CODE_MISMATCH',
  'INVALID_PASSWORD',
  'INCORRECT_CURRENT_PASSWORD',
  'MFA_STEP_UP_EXPIRED',
]);

/**
 * Re-throw a refused step-up write in plain words, keeping its code, so the
 * step-up dialog (which shows any other error's message as it is) explains it.
 */
export function plainStepUpError(err: unknown): never {
  const code = namedLabelErrorCode(err);
  if (err instanceof StepUpFetchError && code && !STEP_UP_CODES.has(code)) {
    throw new StepUpFetchError(describeWorkspaceGroupError(err), err.status, code);
  }
  throw err;
}

function useInvalidateGroups(holderId: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.vault.workspaceGroupsAll(holderId) }),
      qc.invalidateQueries({ queryKey: queryKeys.vault.namedLabelsAll(holderId) }),
    ]);
}

/**
 * For a narrowing through a group — removing someone, leaving, taking a group
 * off a label: the groups and labels AND the workspace's documents, as the
 * label module's own narrowings do (`useInvalidateNamedLabelsAndDocuments`).
 * The caller may have been reading labelled documents through that group.
 * Document details are REMOVED, not merely marked stale: a stale entry still
 * renders its name and text until a refetch replaces it.
 */
function useInvalidateGroupsAndDocuments(holderId: string) {
  const qc = useQueryClient();
  const invalidateGroups = useInvalidateGroups(holderId);
  return () => {
    qc.removeQueries({ queryKey: queryKeys.vaultDocuments.details() });
    return Promise.all([
      invalidateGroups(),
      qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.forHolder(holderId) }),
    ]);
  };
}

function workspaceGroupsQuery(holderId: string | null) {
  return {
    queryKey: queryKeys.vault.workspaceGroups(holderId ?? ''),
    queryFn: () => api.listVaultWorkspaceGroups(holderId as string),
    enabled: !!holderId,
    retry: false,
  } as const;
}

/** Every group in the workspace, by name; counts only where the caller may see them. */
export function useVaultWorkspaceGroups(holderId: string | null) {
  return useQuery({ ...workspaceGroupsQuery(holderId), select: (data) => data.groups });
}

/**
 * Whether to offer "New group" here: the server's answer (an owner or admin,
 * and named labels in use here or allowed to start — groups exist to read
 * them), so the screen never shows a form the server would refuse.
 */
export function useCanCreateVaultWorkspaceGroup(holderId: string | null) {
  return useQuery({ ...workspaceGroupsQuery(holderId), select: (data) => data.canCreate });
}

/** A group's members (owners, admins and the group's own members; others get NotFound). */
export function useVaultWorkspaceGroupMembers(
  holderId: string,
  groupId: string | null,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.vault.workspaceGroupMembers(holderId, groupId ?? ''),
    queryFn: async () =>
      (await api.listVaultWorkspaceGroupMembers(holderId, groupId as string)).members,
    enabled: !!groupId && (options.enabled ?? true),
    retry: false,
  });
}

/** The groups a label reads (its readers, and owners and admins). */
export function useVaultNamedLabelGroups(
  holderId: string,
  labelId: string | null,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.vault.namedLabelGroups(holderId, labelId ?? ''),
    queryFn: async () => (await api.listVaultNamedLabelGroups(holderId, labelId as string)).groups,
    enabled: !!labelId && (options.enabled ?? true),
    retry: false,
  });
}

/**
 * Who a group would let read a label, for a manager of the label to see before
 * adding it. Fetched fresh each time they choose to look: the add is pinned to
 * exactly this list. Every answer is an audited look, so its key sits outside
 * the labels prefix and other writes on the page never refetch it.
 */
export function useVaultNamedLabelGroupPreview(
  holderId: string,
  labelId: string,
  groupId: string | null,
) {
  return useQuery({
    queryKey: queryKeys.vault.namedLabelGroupPreview(holderId, labelId, groupId ?? ''),
    queryFn: () => api.previewVaultNamedLabelGroup(holderId, labelId, groupId as string),
    enabled: !!groupId,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    // What the manager reviews must not change under them: no refetch on its
    // own (focus, reconnect). The screen also freezes what was reviewed.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useCreateVaultWorkspaceGroup(holderId: string) {
  const invalidate = useInvalidateGroups(holderId);
  return useMutation({
    mutationFn: async (body: { name: string; description?: string | null }) =>
      (await api.createVaultWorkspaceGroup(holderId, body)).group,
    onSuccess: invalidate,
  });
}

export function useUpdateVaultWorkspaceGroup(holderId: string) {
  const invalidate = useInvalidateGroups(holderId);
  return useMutation({
    mutationFn: async (input: { groupId: string; name?: string; description?: string | null }) => {
      const { groupId, ...body } = input;
      return (await api.updateVaultWorkspaceGroup(holderId, groupId, body)).group;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteVaultWorkspaceGroup(holderId: string) {
  const invalidate = useInvalidateGroups(holderId);
  return useMutation({
    mutationFn: (groupId: string) => api.deleteVaultWorkspaceGroup(holderId, groupId),
    onSuccess: invalidate,
  });
}

export function useSetVaultWorkspaceGroupManager(holderId: string) {
  const invalidate = useInvalidateGroups(holderId);
  return useMutation({
    mutationFn: (input: { groupId: string; principalId: string; isManager: boolean }) =>
      api.setVaultWorkspaceGroupManager(
        holderId,
        input.groupId,
        input.principalId,
        input.isManager,
      ),
    onSuccess: invalidate,
  });
}

/** Remove someone from a group — or, with your own principal id, leave it (a narrowing). */
export function useRemoveVaultWorkspaceGroupMember(holderId: string) {
  const invalidate = useInvalidateGroupsAndDocuments(holderId);
  return useMutation({
    mutationFn: (input: { groupId: string; principalId: string }) =>
      api.removeVaultWorkspaceGroupMember(holderId, input.groupId, input.principalId),
    onSuccess: invalidate,
  });
}

/** Take a group off a label (a narrowing). */
export function useRemoveVaultNamedLabelGroup(holderId: string) {
  const invalidate = useInvalidateGroupsAndDocuments(holderId);
  return useMutation({
    mutationFn: (input: { labelId: string; groupId: string }) =>
      api.removeVaultNamedLabelGroup(holderId, input.labelId, input.groupId),
    onSuccess: invalidate,
  });
}

/** Invalidate after a step-up write that bypassed react-query's mutation layer. */
export function useVaultWorkspaceGroupsInvalidator(holderId: string) {
  return useInvalidateGroups(holderId);
}

// ── Step-up writes: untraced, with the person's proof ────────────────────────

/**
 * Add someone to a group. Sent first with no proof (`creds` null): a group
 * that reads no label takes it at once. A group that reads a label is a
 * widening of that label, so the server refuses `STEP_UP_REQUIRED` and the
 * caller asks for proof and sends it again.
 */
export async function addVaultWorkspaceGroupMember(
  holderId: string,
  groupId: string,
  body: { principalId: string; isManager?: boolean },
  creds: StepUpCredentials | null,
) {
  return stepUpFetch<VaultWorkspaceGroupMemberAddedDto>(
    `${vaultWorkspaceGroupPath(holderId, groupId)}/members`,
    { method: 'POST', body: { ...body, ...(creds ? proof(creds) : {}) } },
  );
}

/**
 * Put a group on a label (a widening a manager of the label authorises).
 * `expectedMemberIds` are the people the manager was shown in the preview: if
 * the group's members changed since, the server refuses
 * `NAMED_LABEL_GROUP_CHANGED` and nothing happens.
 */
export async function addVaultNamedLabelGroup(
  holderId: string,
  labelId: string,
  body: { groupId: string; expectedMemberIds: readonly string[] },
  creds: StepUpCredentials,
) {
  return stepUpFetch<{ group: VaultNamedLabelGroupDto }>(
    `${vaultNamedLabelPath(holderId, labelId)}/groups`,
    {
      method: 'POST',
      body: {
        groupId: body.groupId,
        expectedMemberIds: [...body.expectedMemberIds],
        ...proof(creds),
      },
    },
  );
}

/** What to tell someone who just added a member to a group. */
export function addedToGroupMessage(added: { labelsWaiting: number }): string {
  const n = added.labelsWaiting;
  if (n > 0) {
    return `Added. They’ll be able to read ${n} label${n === 1 ? '' : 's'} once each label’s manager approves.`;
  }
  return 'Added.';
}
